import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useRealtimeSelfHeal } from '@/hooks/useRealtimeSelfHeal';

export interface LaneData {
  name: string;
  shortName: string;
  rate: number;
  rateDisplay: string;
  volume: number;
  fill: string;
  isTop: boolean;
  totalMiles: number;
  totalAmount: number;
  originCity: string;
  originState: string;
  destCity: string;
  destState: string;
}

export interface LaneStats {
  lanes: LaneData[];
  globalAverage: number;
  availableEquipmentTypes: string[];
  isLoading: boolean;
  error: string | null;
}

// --- Helpers ---

const isValidRate = (rate: unknown): rate is number => {
  if (rate === null || rate === undefined) return false;
  if (typeof rate === 'string') {
    const parsed = parseFloat(rate);
    return !isNaN(parsed) && isFinite(parsed) && parsed > 0;
  }
  if (typeof rate === 'number') return !isNaN(rate) && isFinite(rate) && rate > 0;
  return false;
};

const parseRate = (rate: unknown): number => {
  if (typeof rate === 'number') return rate;
  if (typeof rate === 'string') return parseFloat(rate);
  return 0;
};

const formatLaneName = (oC: string, oS: string, dC: string, dS: string) =>
  `${oC || 'Unknown'}, ${oS || 'Unknown'} → ${dC || 'Unknown'}, ${dS || 'Unknown'}`;

const formatShortName = (name: string) => {
  if (name.length <= 25) return name;
  const parts = name.split(' → ');
  if (parts.length === 2) {
    const s = (s: string) => (s.length > 12 ? s.substring(0, 12) + '...' : s);
    return `${s(parts[0])} → ${s(parts[1])}`;
  }
  return name.substring(0, 22) + '...';
};

const todayDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// --- Fetch: single direct query on active board loads ---

const fetchLaneData = async (equipmentType?: string, minMiles?: number, maxMiles?: number) => {
  const today = todayDateString();

  let q = supabase
    .from('loads')
    .select('origin_city, origin_state, destination_city, destination_state, total_miles, rate_amount')
    .eq('status', 'posted')
    .eq('is_public', true)
    .not('total_miles', 'is', null)
    .not('rate_amount', 'is', null)
    .gt('total_miles', 0)
    .gt('rate_amount', 0)
    .gte('pickup_date', today)
    .order('posted_at', { ascending: false })
    .limit(500);

  if (equipmentType) q = q.eq('equipment_type', equipmentType as any);
  if (minMiles) q = q.gte('total_miles', minMiles);
  if (maxMiles) q = q.lte('total_miles', maxMiles);

  const { data: loads, error } = await q;
  if (error) throw error;
  if (!loads?.length) return { lanes: [] as LaneData[], globalAverage: 0 };

  // Group by lane
  const laneGroups = new Map<string, { totalAmount: number; totalMiles: number; count: number; originCity: string; originState: string; destCity: string; destState: string }>();

  loads.forEach(load => {
    if (!isValidRate(load.rate_amount) || !isValidRate(load.total_miles)) return;

    const amount = parseRate(load.rate_amount);
    const miles = parseRate(load.total_miles);
    const key = `${load.origin_city}-${load.origin_state}-${load.destination_city}-${load.destination_state}`;

    if (!laneGroups.has(key)) {
      laneGroups.set(key, {
        totalAmount: 0, totalMiles: 0, count: 0,
        originCity: load.origin_city || 'Unknown',
        originState: load.origin_state || 'Unknown',
        destCity: load.destination_city || 'Unknown',
        destState: load.destination_state || 'Unknown',
      });
    }

    const g = laneGroups.get(key)!;
    g.totalAmount += amount;
    g.totalMiles += miles;
    g.count++;
  });

  const laneDataArray: LaneData[] = [];
  let totalRateSum = 0;
  let totalCount = 0;

  laneGroups.forEach((g) => {
    if (g.count > 0 && g.totalMiles > 0) {
      const avgRpm = g.totalAmount / g.totalMiles;
      if (!isFinite(avgRpm) || isNaN(avgRpm)) return;

      const laneName = formatLaneName(g.originCity, g.originState, g.destCity, g.destState);
      laneDataArray.push({
        name: laneName,
        shortName: formatShortName(laneName),
        rate: avgRpm,
        rateDisplay: `$${avgRpm.toFixed(2)}`,
        volume: g.count,
        fill: 'url(#regularGradient)',
        isTop: false,
        totalMiles: g.totalMiles,
        totalAmount: g.totalAmount,
        originCity: g.originCity,
        originState: g.originState,
        destCity: g.destCity,
        destState: g.destState,
      });
      totalRateSum += avgRpm;
      totalCount++;
    }
  });

  laneDataArray.sort((a, b) => b.rate - a.rate);
  const top5 = laneDataArray.slice(0, 5);
  if (top5.length > 0) { top5[0].isTop = true; top5[0].fill = 'url(#topGradient)'; }

  return { lanes: top5, globalAverage: totalCount > 0 ? totalRateSum / totalCount : 0 };
};

const fetchEquipmentTypes = async (): Promise<string[]> => {
  const today = todayDateString();
  const { data } = await supabase
    .from('loads')
    .select('equipment_type')
    .eq('status', 'posted')
    .eq('is_public', true)
    .gte('pickup_date', today)
    .not('total_miles', 'is', null)
    .gt('total_miles', 0);
  return data ? [...new Set(data.map(l => l.equipment_type))].filter(Boolean) : [];
};

// --- Hook ---

export const useLaneData = (equipmentType?: string, minMiles?: number, maxMiles?: number): LaneStats => {
  const queryClient = useQueryClient();

  const { data: eqTypes = [] } = useQuery({
    queryKey: ['lane-equipment-types'],
    queryFn: fetchEquipmentTypes,
    staleTime: 120_000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['lane-data', equipmentType, minMiles, maxMiles],
    queryFn: () => fetchLaneData(equipmentType, minMiles, maxMiles),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000, // auto-refresh every 5 minutes
  });

  // Realtime invalidation
  useEffect(() => {
    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['lane-data'] });
    const channel = supabase
      .channel('lane-data-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'loads' }, () => {
        setTimeout(invalidate, 1000);
      })
      .subscribe((status) => { if (status === 'SUBSCRIBED') invalidate(); });

    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  useRealtimeSelfHeal(() => queryClient.invalidateQueries({ queryKey: ['lane-data'] }));


  return {
    lanes: data?.lanes ?? [],
    globalAverage: data?.globalAverage ?? 0,
    availableEquipmentTypes: eqTypes,
    isLoading,
    error: error ? (error as Error).message : null,
  };
};
